import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { DraggableBlockPlugin_EXPERIMENTAL } from '@lexical/react/LexicalDraggableBlockPlugin';
import {
  $createParagraphNode,
  $getNearestNodeFromDOMNode,
  $getRoot,
  $getSelection,
  $isParagraphNode,
  $isRangeSelection,
} from 'lexical';
import { PlusIcon } from '@heroicons/react/20/solid';
import { Command as CommandPrimitive } from 'cmdk';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@dashboard/components/ui/tooltip';

import GripVerticalIcon from '../icons/GripVerticalIcon';
import { blockGroups, blockOptions } from './blockOptions';
import { CLOSE_SLASH_MENU_COMMAND } from './SlashCommandPlugin';

const DRAGGABLE_MENU_CLASS = 'cp-draggable-block-menu';
const HIDDEN_CLASS = 'is-hidden';
const POPOVER_OFFSET = 4;
const POPOVER_WIDTH = 240;
const MODIFIER_KEYS = new Set(['Meta', 'Control', 'Alt', 'Shift', 'CapsLock']);

function prepareTargetParagraph(editor, targetElem) {
  editor.update(() => {
    const node = $getNearestNodeFromDOMNode(targetElem);
    if (!node) return;

    const isEmptyParagraph = $isParagraphNode(node) && node.getTextContent().trim() === '';
    let targetParagraph;
    if (isEmptyParagraph) {
      targetParagraph = node;
    } else {
      targetParagraph = $createParagraphNode();
      if (node.getParent() === $getRoot()) {
        node.insertAfter(targetParagraph);
      } else {
        $getRoot().append(targetParagraph);
      }
    }

    targetParagraph.select();
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) return;
  });
}

function isEmptyParagraph(element) {
  return !!element && element.tagName === 'P' && element.textContent === '';
}

function getEmptyEditorElement(editor) {
  const root = editor.getRootElement();
  if (!root || root.children.length !== 1) return null;
  const first = root.firstElementChild;
  return isEmptyParagraph(first) ? first : null;
}

// Container blocks (lists, accordions) align to their first child. Text
// blocks must not: their first child is an inline span, and its rect is
// the glyph box, not the line box, which drops the handle 2px too low.
export function firstLineBox(blockElem) {
  const firstChild = blockElem.firstElementChild;
  const elem =
    firstChild && getComputedStyle(firstChild).display !== 'inline' ? firstChild : blockElem;
  const rect = elem.getBoundingClientRect();
  const style = getComputedStyle(elem);
  const padTop = parseFloat(style.paddingTop) || 0;
  const contentHeight = rect.height - padTop - (parseFloat(style.paddingBottom) || 0);
  let lineHeight = parseInt(style.lineHeight, 10);
  if (isNaN(lineHeight) || lineHeight <= 0 || lineHeight > contentHeight) {
    lineHeight = contentHeight;
  }
  return { top: rect.top + padTop, bottom: rect.top + padTop + lineHeight, lineHeight };
}

function centerMenuOn(menu, anchorElem, blockElem) {
  const anchorRect = anchorElem.getBoundingClientRect();
  const menuRect = menu.getBoundingClientRect();
  if (!menuRect.height) return;

  const { top, lineHeight } = firstLineBox(blockElem);
  const centeredTop = top - anchorRect.top + (lineHeight - menuRect.height) / 2;

  const current = menu.style.transform || '';
  const m = current.match(/translate\(([^,]+)px,\s*([^)]+)px\)/);
  const x = m ? parseFloat(m[1]) : 4;
  const nextTransform = `translate(${x}px, ${centeredTop}px)`;
  if (nextTransform !== current) menu.style.transform = nextTransform;
}

export default function DraggableBlockPlugin({ anchorElem }) {
  const [editor] = useLexicalComposerContext();
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const targetLineRef = useRef(null);
  const [targetElem, setTargetElem] = useState(null);
  const [popover, setPopover] = useState(null);

  // Lexical centres the menu before it has a rendered height, so it lands on the
  // block's top edge. Re-centre after layout with the real height. An empty
  // editor also pins the handle to its one line without hover, like Medium, so
  // the gutter is not a blank band and the + is discoverable.
  const pinnedRef = useRef(null);
  const typingHiddenRef = useRef(false);
  const popoverOpenRef = useRef(false);
  useEffect(() => {
    if (!menuRef.current || !anchorElem) return undefined;
    const menu = menuRef.current;
    let rafId = 0;
    const place = () => {
      if (popoverOpenRef.current) return;
      const pinned = targetElem ? null : getEmptyEditorElement(editor);
      const block = pinned || targetElem;
      if (pinned) {
        typingHiddenRef.current = false;
        menu.style.display = 'flex';
        menu.style.opacity = '1';
        menu.classList.remove(HIDDEN_CLASS);
      } else if (pinnedRef.current && !targetElem) {
        menu.style.display = 'none';
      }
      pinnedRef.current = pinned;
      if (block) centerMenuOn(menu, anchorElem, block);
    };
    // Next frame, so Lexical's own transform has landed before we override it.
    const schedule = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(place);
    };
    schedule();
    const unregister = editor.registerUpdateListener(schedule);
    return () => {
      cancelAnimationFrame(rafId);
      unregister();
    };
  }, [targetElem, anchorElem, editor]);

  // Lexical re-targets the handle on every mousemove, so during a selection drag
  // it chases the pointer across blocks. isOnMenu is the only hook that makes
  // Lexical skip a move, so it doubles as the freeze while a button is held.
  const isSelectingRef = useRef(false);
  const isOnMenu = useCallback(
    element => isSelectingRef.current || !!element.closest(`.${DRAGGABLE_MENU_CLASS}`),
    [],
  );

  // Lexical shows and hides the menu by flipping inline display, which cannot
  // animate. The stylesheet pins display and this mirrors every flip (and the
  // typing hide) into one class, so all show/hide paths share the same fade.
  useEffect(() => {
    if (!anchorElem) return undefined;
    const menu = menuRef.current;
    const scroller = anchorElem.parentElement || anchorElem;
    const editable = anchorElem.querySelector('[contenteditable="true"]');
    if (!menu || !editable) return undefined;

    const apply = () => {
      menu.classList.toggle(HIDDEN_CLASS, menu.style.display === 'none' || typingHiddenRef.current);
    };
    const observer = new MutationObserver(apply);
    observer.observe(menu, { attributes: true, attributeFilter: ['style'] });
    apply();

    const hide = event => {
      if (MODIFIER_KEYS.has(event.key)) return;
      typingHiddenRef.current = true;
      apply();
    };
    const show = () => {
      typingHiddenRef.current = false;
      apply();
    };
    const startSelecting = event => {
      if (event.button === 0) isSelectingRef.current = true;
    };
    const stopSelecting = () => {
      isSelectingRef.current = false;
    };

    editable.addEventListener('keydown', hide);
    editable.addEventListener('mousedown', startSelecting);
    document.addEventListener('mouseup', stopSelecting);
    scroller.addEventListener('mousemove', show);
    return () => {
      observer.disconnect();
      editable.removeEventListener('keydown', hide);
      editable.removeEventListener('mousedown', startSelecting);
      document.removeEventListener('mouseup', stopSelecting);
      scroller.removeEventListener('mousemove', show);
    };
  }, [anchorElem]);

  const grouped = useMemo(
    () =>
      blockGroups
        .map(group => ({ ...group, items: blockOptions.filter(o => o.group === group.key) }))
        .filter(group => group.items.length > 0),
    [],
  );

  useEffect(() => {
    popoverOpenRef.current = popover !== null;
  }, [popover]);

  const openPopover = useCallback(() => {
    const block = pinnedRef.current || targetElem;
    if (!triggerRef.current || !block || !anchorElem) return;
    const editable = anchorElem.querySelector('[contenteditable="true"]');
    const contentLeft =
      anchorElem.getBoundingClientRect().left +
      (editable ? parseFloat(getComputedStyle(editable).paddingLeft) || 0 : 0);
    editor.dispatchCommand(CLOSE_SLASH_MENU_COMMAND, undefined);
    const line = firstLineBox(block);
    setPopover({
      anchorRect: { top: line.top, bottom: line.bottom },
      contentLeft,
      targetElem: block,
    });
  }, [targetElem, anchorElem, editor]);

  const closePopover = useCallback(() => {
    setPopover(null);
  }, []);

  const handleSelect = useCallback(
    option => {
      if (!popover) return;
      prepareTargetParagraph(editor, popover.targetElem);
      editor.focus();
      option.onSelect(editor);
      closePopover();
    },
    [editor, popover, closePopover],
  );

  return (
    <>
      <DraggableBlockPlugin_EXPERIMENTAL
        anchorElem={anchorElem}
        menuRef={menuRef}
        targetLineRef={targetLineRef}
        menuComponent={
          <div
            ref={menuRef}
            className={`${DRAGGABLE_MENU_CLASS} absolute left-0 top-0 flex h-5 items-center will-change-transform`}
          >
            <TooltipProvider>
              <GutterTooltip label="Add a block">
                <button
                  ref={triggerRef}
                  type="button"
                  aria-label="Add a block"
                  onMouseDown={e => e.preventDefault()}
                  onDragStart={e => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onClick={openPopover}
                  className="flex items-center justify-center h-5 w-5 rounded hover:bg-gray-100 dark:hover:bg-muted text-gray-400 dark:text-muted-foreground hover:text-gray-700 dark:hover:text-muted-foreground"
                >
                  <PlusIcon className="w-4 h-4" />
                </button>
              </GutterTooltip>
              <GutterTooltip label="Drag to reorder">
                <button
                  type="button"
                  draggable
                  aria-label="Drag to reorder"
                  className="flex items-center justify-center h-5 w-5 rounded hover:bg-gray-100 dark:hover:bg-muted text-gray-400 dark:text-muted-foreground hover:text-gray-700 dark:hover:text-muted-foreground cursor-grab active:cursor-grabbing"
                >
                  <GripVerticalIcon className="w-4 h-4" />
                </button>
              </GutterTooltip>
            </TooltipProvider>
          </div>
        }
        targetLineComponent={
          <div
            ref={targetLineRef}
            className="cp-draggable-block-target-line pointer-events-none absolute top-0 left-0 bg-gray-400 h-0.5 opacity-0"
          />
        }
        isOnMenu={isOnMenu}
        onElementChanged={setTargetElem}
      />

      {popover &&
        createPortal(
          <BlockInsertPopover
            anchorRect={popover.anchorRect}
            contentLeft={popover.contentLeft}
            grouped={grouped}
            onSelect={handleSelect}
            onClose={closePopover}
          />,
          document.body,
        )}
    </>
  );
}

// Radix starts the open timer on the first pointer move and never cancels it on
// pointer down, so a drag that starts inside the delay shows the tooltip mid-drag.
function GutterTooltip({ label, children }) {
  const [open, setOpen] = useState(false);
  const pressedRef = useRef(false);

  const onOpenChange = useCallback(next => {
    if (next && pressedRef.current) return;
    setOpen(next);
  }, []);

  const onPointerDown = () => {
    pressedRef.current = true;
    setOpen(false);
    const release = () => {
      pressedRef.current = false;
      document.removeEventListener('pointerup', release);
      document.removeEventListener('pointercancel', release);
      document.removeEventListener('dragend', release);
    };
    document.addEventListener('pointerup', release);
    document.addEventListener('pointercancel', release);
    document.addEventListener('dragend', release);
  };

  return (
    <Tooltip open={open} onOpenChange={onOpenChange} disableHoverableContent>
      <TooltipTrigger asChild onPointerDown={onPointerDown}>
        {children}
      </TooltipTrigger>
      <TooltipContent side="bottom" align="start">
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

// Like Notion: the menu opens under the + with its left edge on the text
// column's left edge, and flips above when the window has no room below.
function BlockInsertPopover({ anchorRect, contentLeft, grouped, onSelect, onClose }) {
  const popoverRef = useRef(null);
  const commandRef = useRef(null);
  const [position, setPosition] = useState({ top: anchorRect.bottom + POPOVER_OFFSET, left: 0 });

  useLayoutEffect(() => {
    const height = popoverRef.current ? popoverRef.current.offsetHeight : 0;
    const fitsBelow = anchorRect.bottom + POPOVER_OFFSET + height <= window.innerHeight - 8;
    const top = fitsBelow
      ? anchorRect.bottom + POPOVER_OFFSET
      : Math.max(8, anchorRect.top - POPOVER_OFFSET - height);
    const left = Math.max(8, contentLeft);
    setPosition({ top: top + window.scrollY, left: left + window.scrollX });
  }, [anchorRect, contentLeft]);

  useEffect(() => {
    commandRef.current?.focus({ preventScroll: true });
    function handlePointerDown(event) {
      if (popoverRef.current && !popoverRef.current.contains(event.target)) {
        onClose();
      }
    }
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [onClose]);

  return (
    <div
      ref={popoverRef}
      style={{
        position: 'absolute',
        top: position.top,
        left: position.left,
        width: POPOVER_WIDTH,
        zIndex: 50,
      }}
      className="cp-block-menu rounded-md border border-gray-100 dark:border-border bg-white dark:bg-popover shadow-lg text-sm"
    >
      <CommandPrimitive
        ref={commandRef}
        tabIndex={-1}
        loop
        className="outline-none"
        onKeyDown={e => {
          if (e.key === 'Escape') {
            e.preventDefault();
            onClose();
          }
        }}
      >
        <CommandPrimitive.List className="max-h-80 overflow-y-auto py-1">
          {grouped.map(group => (
            <CommandGroup
              key={group.key}
              heading={group.heading}
              items={group.items}
              onSelect={onSelect}
            />
          ))}
        </CommandPrimitive.List>
      </CommandPrimitive>
    </div>
  );
}

function CommandGroup({ heading, items, onSelect }) {
  return (
    <CommandPrimitive.Group className="not-first:mt-1">
      <div className="px-3 py-1 text-xs text-gray-500 dark:text-muted-foreground">{heading}</div>
      {items.map(option => (
        <CommandPrimitive.Item
          key={option.key}
          value={option.key}
          onSelect={() => onSelect(option)}
          className="flex items-center gap-2 px-3 py-1.5 text-gray-700 dark:text-muted-foreground cursor-pointer outline-none data-[selected=true]:bg-gray-100 dark:data-[selected=true]:bg-accent data-[selected=true]:text-black dark:data-[selected=true]:text-foreground"
        >
          <option.icon className="w-4 h-4 flex-shrink-0" />
          <span className="truncate">{option.label}</span>
        </CommandPrimitive.Item>
      ))}
    </CommandPrimitive.Group>
  );
}
